import PropTypes from 'prop-types';

import { Box, Button } from '@mui/material';

import GenericFormContainer from '../Forms/GenericFormContainer';

import {
  formatSelect,
  lengthValidation120,
  lengthValidation1000,
  notNullOrUndefined,
  required,
} from '../Forms/utils';

import {
  ANNOUNCEMENTS_ACCESS_CONTROL_SYSTEMS,
  ANNOUNCEMENTS_VIDEO_STREAM_SERVICES,
} from './constants';

import {
  CHECKBOX,
  DATE_PICKER,
  MESSAGE,
  REACT_SELECT_INPUT,
  STYLED_TEXT,
} from '../Forms/fieldTypes';

const AnnouncementsFormComponent = (props) => {
  const {
    pmcs,
    buildings,
    formValues,
    onSave,
    onDelete,
    initialValues,
    validate,
    form,
    onSubmit,
    onLoad,
  } = props;

  const { id } = formValues;
  const allProperties = false;
  const { sent, pmcsOptions, buildingsOptions } = initialValues;
  const formData = [
    {
      keyName: 'all_properties', // allProperties
      label: 'Select All Properties',
      fieldType: CHECKBOX,
      validate: [],
    },
    {
      keyName: 'pmcs',
      label: 'PMCs',
      fieldType: REACT_SELECT_INPUT,
      options: formatSelect(sent ? pmcsOptions : pmcs),
      multi: true,
      validate: [],
      isHidden: allProperties,
    },
    {
      keyName: 'buildings',
      label: 'Buildings',
      fieldType: REACT_SELECT_INPUT,
      options: formatSelect(sent ? buildingsOptions : buildings),
      multi: true,
      validate: allProperties ? [] : [required],
      isHidden: allProperties,
    },
    {
      keyName: 'access_control_system',
      label: 'Access Control System',
      fieldType: REACT_SELECT_INPUT,
      options: ANNOUNCEMENTS_ACCESS_CONTROL_SYSTEMS,
      validate: [],
      isHidden: allProperties,
      isClearable: true,
    },
    {
      keyName: 'camera_system',
      label: 'Video Stream Service',
      fieldType: REACT_SELECT_INPUT,
      options: ANNOUNCEMENTS_VIDEO_STREAM_SERVICES,
      validate: [],
      isHidden: allProperties,
      isClearable: true,
    },
    {
      keyName: 'dateLaunchedMessage',
      fieldType: MESSAGE,
      value: 'Building launched:',
      isHidden: allProperties,
    },
    {
      keyName: 'launch_date_after',
      label: 'After',
      fieldType: DATE_PICKER,
      validate: [],
      disabled: sent,
      isHidden: allProperties,
      secondKeyName: 'launch_date_before',
      secondLabel: 'Before',
    },
    {
      keyName: 'subject',
      label: 'Message Title',
      fieldType: STYLED_TEXT,
      validate: [required, lengthValidation120],
    },
    {
      keyName: 'message',
      label: 'Message',
      fieldType: STYLED_TEXT,
      multiline: true,
      validate: [required, lengthValidation1000],
    },
    {
      keyName: 'rolesTitleMessage',
      fieldType: MESSAGE,
      value: 'Display pop-up message to:',
    },
    {
      keyName: 'roles.pmc_owner',
      label: 'PMC Owners',
      fieldType: CHECKBOX,
      validate: [notNullOrUndefined],
    },
    {
      keyName: 'roles.property_manager',
      label: 'Property Managers',
      fieldType: CHECKBOX,
      validate: [notNullOrUndefined],
    },
    {
      keyName: 'roles.superintendent',
      label: 'Supers',
      fieldType: CHECKBOX,
      validate: [notNullOrUndefined],
    },
    {
      keyName: 'roles.handyman',
      label: 'Handymen',
      fieldType: CHECKBOX,
      validate: [notNullOrUndefined],
    },
    {
      keyName: 'roles.concierge',
      label: 'Concierge',
      fieldType: CHECKBOX,
      validate: [notNullOrUndefined],
    },
    {
      keyName: 'roles.owner',
      label: 'Owners',
      fieldType: CHECKBOX,
      validate: [notNullOrUndefined],
    },
    {
      keyName: 'roles.board_member',
      label: 'Board Members',
      fieldType: CHECKBOX,
      validate: [notNullOrUndefined],
    },
    {
      keyName: 'roles.tenant',
      label: 'Tenants',
      fieldType: CHECKBOX,
      validate: [notNullOrUndefined],
    },
  ].map((formField) => ({
    ...formField,
    disabled:
      formField?.disabled ??
      (sent &&
        formField?.keyName !== 'subject' &&
        formField?.keyName !== 'message'),
  }));

  const actionButtons = ({ disabled }) => (
    <Box display="flex">
      {!sent && (
        <Box mr="1rem">
          <Button
            type="button"
            variant="contained"
            color="primary"
            disabled={disabled}
            disableElevation
            onClick={onSave}
            onMouseDown={(e) => e.preventDefault()} // workaround for the 'second click' issue - https://github.com/redux-form/redux-form/issues/860#issuecomment-625254444
          >
            Save (to send later)
          </Button>
        </Box>
      )}

      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={disabled}
        disableElevation
        onMouseDown={(e) => e.preventDefault()}
      >
        {sent ? 'Update' : 'Send now'}
      </Button>
      <Box ml="1rem" />
      {id ? (
        <Button
          type="button"
          variant="contained"
          color="secondary"
          disabled={disabled}
          disableElevation
          onClick={() => onDelete(id)}
          onMouseDown={(e) => e.preventDefault()}
        >
          Delete
        </Button>
      ) : null}
    </Box>
  );

  return (
    <GenericFormContainer
      form={form}
      formData={formData}
      onSubmit={onSubmit}
      initialValues={initialValues}
      onLoad={onLoad}
      title="Pop-up message"
      CustomComponent={actionButtons}
      disabled={false}
      isHideSubmitButton
      backButton={false}
      isFullWidthForm
      validate={validate}
      enableReinitialize
    />
  );
};

AnnouncementsFormComponent.defaultProps = {};

AnnouncementsFormComponent.propTypes = {
  form: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  pmcs: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
  buildings: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
  onDelete: PropTypes.func.isRequired,
  formValues: PropTypes.instanceOf(Object).isRequired,
  initialValues: PropTypes.instanceOf(Object).isRequired,
  onSave: PropTypes.func.isRequired,
  validate: PropTypes.func.isRequired,
  onLoad: PropTypes.func.isRequired,
};

export default AnnouncementsFormComponent;
